import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Phone,
  PhoneOff,
  Mic,
  MicOff,
  Volume2,
  VolumeX,
  AlertCircle,
  CheckCircle,
  MessageSquare,
  User,
  Bot,
  BookOpen,
  FileText,
} from "lucide-react";
import webAuthService from "../services/webAuthService";
import { setMeetingActive } from "../services/meetingSession";
import { apiClient } from "../services/api";

// Note: Azure Speech SDK is loaded via CDN in index.html
// Available globally as window.SpeechSDK

/**
 * CallingAgent - Real-time voice interaction with ElevenLabs AI
 *
 * Features:
 * - Real-time audio-to-text using Azure Speech SDK (reused from NewMeeting)
 * - WebSocket communication with backend
 * - ElevenLabs TTS audio playback
 * - Live conversation transcript
 */
const CallingAgent = () => {
  const [isCallActive, setIsCallActive] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isSpeakerMuted, setIsSpeakerMuted] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState("disconnected");
  const [transcripts, setTranscripts] = useState([]);
  const [selectedPersona, setSelectedPersona] = useState("sales"); // 'sales' or 'client'
  const [status, setStatus] = useState({
    type: "info",
    message: "Ready to start call. Click 'Start Call' to begin...",
  });
  const [audioLevel, setAudioLevel] = useState(0);
  const [userId, setUserId] = useState(null);
  const [knowledgeBaseFiles, setKnowledgeBaseFiles] = useState([]);
  const [selectedKBFiles, setSelectedKBFiles] = useState([]);
  const [loadingKB, setLoadingKB] = useState(false);
  const [externalCollections, setExternalCollections] = useState([]);
  const [selectedExtCollections, setSelectedExtCollections] = useState([]);

  // Audio streaming with Web Audio API for truly gapless playback
  const audioContextRef = useRef(null);
  const nextPlayTimeRef = useRef(0);
  const chunkAccumulatorRef = useRef([]);
  const accumulatorTimerRef = useRef(null);
  const combineChunkCount = 3; // Combine chunks to reduce decoding overhead

  // Chunk sequencing to fix out-of-order audio issues
  const chunkBufferRef = useRef(new Map()); // chunk_number -> chunk_data
  const nextExpectedChunkRef = useRef(1);
  const processingChunksRef = useRef(false);

  // Refs for persistent objects
  const recognizerRef = useRef(null);
  const websocketRef = useRef(null);
  const micStreamRef = useRef(null);
  const analyserRef = useRef(null);
  const transcriptContainerRef = useRef(null);
  const sessionIdRef = useRef(null);
  const selectedKBFilesRef = useRef(selectedKBFiles);
  const selectedExtCollectionsRef = useRef(selectedExtCollections);

  // Keep the ref in sync so async callbacks never read stale state
  useEffect(() => {
    selectedKBFilesRef.current = selectedKBFiles;
  }, [selectedKBFiles]);

  useEffect(() => {
    selectedExtCollectionsRef.current = selectedExtCollections;
  }, [selectedExtCollections]);

  // Get user ID on mount and fetch KB files
  useEffect(() => {
    const initUser = async () => {
      try {
        const userId = await webAuthService.getUserId();
        if (userId) {
          setUserId(userId);
          fetchKnowledgeBaseFiles(userId);
          fetchExternalCollections(userId);
          console.log("✅ Using authenticated user ID:", userId);
        } else {
          console.warn("⚠️ No authenticated user found");
        }
      } catch (error) {
        console.error("❌ Failed to get user ID:", error);
      }
    };
    initUser();
  }, []);

  const fetchKnowledgeBaseFiles = async (uid) => {
    try {
      setLoadingKB(true);
      const response = await apiClient.get(`/api/knowledge-base/user/${uid}`);
      setKnowledgeBaseFiles(response.data.files || []);
    } catch (error) {
      console.error("Error fetching knowledge base files:", error);
      setKnowledgeBaseFiles([]);
    } finally {
      setLoadingKB(false);
    }
  };

  const fetchExternalCollections = async (uid) => {
    try {
      const response = await apiClient.get(`/api/knowledge-base/external-collections/${uid}`);
      setExternalCollections(response.data.collections || []);
    } catch (error) {
      console.error("Error fetching external collections:", error);
      setExternalCollections([]);
    }
  };

  const toggleKBFile = useCallback((fileId) => {
    setSelectedKBFiles((prev) =>
      prev.includes(fileId)
        ? prev.filter((id) => id !== fileId)
        : [...prev, fileId]
    );
  }, []);

  const toggleExtCollection = useCallback((collectionId) => {
    setSelectedExtCollections((prev) =>
      prev.includes(collectionId)
        ? prev.filter((id) => id !== collectionId)
        : [...prev, collectionId]
    );
  }, []);

  // Auto-scroll transcript container
  useEffect(() => {
    if (transcriptContainerRef.current) {
      transcriptContainerRef.current.scrollTop =
        transcriptContainerRef.current.scrollHeight;
    }
  }, [transcripts]);

  /**
   * Generate a unique session ID for this call
   */
  const generateSessionId = () => {
    return `call_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  /**
   * Start the call - setup WebSocket and audio capture
   */
  const startCall = async () => {
    try {
      if (!userId) {
        setStatus({
          type: "error",
          message: "❌ User ID not available. Please refresh and login again.",
        });
        return;
      }

      setStatus({ type: "info", message: "🎬 Starting call..." });
      setTranscripts([]);
      sessionIdRef.current = generateSessionId();

      // Request microphone access with same config as NewMeeting
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 16000,
          channelCount: 1,
        },
      });
      micStreamRef.current = stream;

      // Setup audio visualization
      setupAudioVisualization(stream);

      // Connect WebSocket
      await connectWebSocket();

      // Setup Azure Speech Recognition
      await setupSpeechRecognition();

      setIsCallActive(true);
      setStatus({ type: "success", message: "✅ Call started" });
    } catch (error) {
      console.error("❌ Failed to start call:", error);
      setStatus({
        type: "error",
        message: `Failed to start call: ${error.message}`,
      });
    }
  };

  /**
   * Stop the call - cleanup all resources
   */
  const stopCall = () => {
    try {
      // Stop speech recognition
      if (recognizerRef.current) {
        recognizerRef.current.stopContinuousRecognitionAsync(
          () => {
            console.log("✅ Speech recognition stopped");
            recognizerRef.current.close();
            recognizerRef.current = null;
          },
          (err) => {
            console.error("❌ Error stopping recognition:", err);
            recognizerRef.current.close();
            recognizerRef.current = null;
          },
        );
      }

      // Close WebSocket
      if (websocketRef.current) {
        websocketRef.current.close();
        websocketRef.current = null;
      }

      // Stop microphone
      if (micStreamRef.current) {
        micStreamRef.current.getTracks().forEach((track) => track.stop());
        micStreamRef.current = null;
      }

      // Stop all audio playback
      stopAllAudio();

      // Close audio context
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }

      setIsCallActive(false);
      setConnectionStatus("disconnected");
      setStatus({ type: "info", message: "Call ended" });
    } catch (error) {
      console.error("❌ Failed to stop call:", error);
    }
  };

  /**
   * Initialize Web Audio Context
   */
  const initAudioContext = () => {
    if (
      !audioContextRef.current ||
      audioContextRef.current.state === "closed"
    ) {
      audioContextRef.current = new (
        window.AudioContext || window.webkitAudioContext
      )();
      nextPlayTimeRef.current = 0;
      console.log("🎵 Web Audio Context initialized for gapless playback");
    }
    if (audioContextRef.current.state === "suspended") {
      audioContextRef.current.resume();
    }
  };

  /**
   * Queue audio chunk with proper sequencing to prevent out-of-order playback
   */
  const queueAudioChunk = async (audioBase64, chunkNumber) => {
    if (!chunkNumber) {
      // Backward compatibility for non-sequenced chunks
      console.log(`🎵 Received non-sequenced chunk, playing immediately`);
      chunkAccumulatorRef.current.push(audioBase64);
      
      if (accumulatorTimerRef.current) {
        clearTimeout(accumulatorTimerRef.current);
      }
      
      if (chunkAccumulatorRef.current.length >= combineChunkCount) {
        await flushAccumulatedChunks();
      } else {
        accumulatorTimerRef.current = setTimeout(async () => {
          await flushAccumulatedChunks();
        }, 100);
      }
      return;
    }

    console.log(`🎵 Buffered chunk #${chunkNumber} (expecting #${nextExpectedChunkRef.current})`);

    // Buffer the chunk
    chunkBufferRef.current.set(chunkNumber, audioBase64);

    // Try to process buffered chunks in sequence
    await processBufferedChunks();
  };

  /**
   * Process buffered chunks in order
   */
  const processBufferedChunks = async () => {
    // Prevent concurrent processing
    if (processingChunksRef.current) {
      return;
    }
    processingChunksRef.current = true;

    try {
      while (true) {
        const nextChunk = chunkBufferRef.current.get(nextExpectedChunkRef.current);
        
        if (!nextChunk) {
          // No more consecutive chunks available
          break;
        }

        console.log(`🔊 Playing chunk #${nextExpectedChunkRef.current} in sequence`);

        // Add to accumulator for smooth playback
        chunkAccumulatorRef.current.push(nextChunk);

        // Remove from buffer
        chunkBufferRef.current.delete(nextExpectedChunkRef.current);
        nextExpectedChunkRef.current++;

        // Clear existing timer
        if (accumulatorTimerRef.current) {
          clearTimeout(accumulatorTimerRef.current);
        }

        // If we have enough chunks, play immediately
        if (chunkAccumulatorRef.current.length >= combineChunkCount) {
          await flushAccumulatedChunks();
        } else {
          // Set timer to flush after short delay
          accumulatorTimerRef.current = setTimeout(async () => {
            await flushAccumulatedChunks();
          }, 100);
        }
      }
    } finally {
      processingChunksRef.current = false;
    }
  };

  /**
   * Combine accumulated chunks and play with Web Audio API (gapless)
   */
  const flushAccumulatedChunks = async () => {
    if (chunkAccumulatorRef.current.length === 0) return;

    const chunksToPlay = [...chunkAccumulatorRef.current];
    chunkAccumulatorRef.current = [];

    console.log(
      `🔄 Playing ${chunksToPlay.length} combined chunks with Web Audio API`,
    );

    try {
      // Initialize audio context
      initAudioContext();
      const audioContext = audioContextRef.current;

      // Combine all chunks into one ArrayBuffer
      const combinedBytes = [];
      for (const chunk of chunksToPlay) {
        const binaryString = atob(chunk);
        for (let i = 0; i < binaryString.length; i++) {
          combinedBytes.push(binaryString.charCodeAt(i));
        }
      }
      const combinedArray = new Uint8Array(combinedBytes);

      // Decode MP3 to audio buffer
      const audioBuffer = await audioContext.decodeAudioData(
        combinedArray.buffer,
      );

      // Create buffer source
      const source = audioContext.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(audioContext.destination);

      // Schedule playback precisely (gapless)
      const currentTime = audioContext.currentTime;
      const playTime = Math.max(currentTime + 0.01, nextPlayTimeRef.current);

      source.start(playTime);

      // Update next play time for perfect continuity
      nextPlayTimeRef.current = playTime + audioBuffer.duration;

      console.log(
        `🔊 Scheduled ${audioBuffer.duration.toFixed(2)}s audio at ${playTime.toFixed(2)}s (next: ${nextPlayTimeRef.current.toFixed(2)}s)`,
      );
    } catch (e) {
      console.error("❌ Failed to play audio:", e);
      // Reset timing on error
      nextPlayTimeRef.current = 0;
    }
  };

  /**
   * Play next chunk (no-op for Web Audio API - scheduling is automatic)
   */
  const playNextChunk = () => {
    // Web Audio API handles scheduling automatically
  };

  /**
   * Flush buffered chunks
   */
  const flushBufferedChunks = () => {
    flushAccumulatedChunks();
  };

  /**
   * Stop all audio playback
   */
  const stopAllAudio = () => {
    // Clear accumulator and timer
    chunkAccumulatorRef.current = [];
    if (accumulatorTimerRef.current) {
      clearTimeout(accumulatorTimerRef.current);
      accumulatorTimerRef.current = null;
    }

    // Clear chunk sequencing buffer
    chunkBufferRef.current.clear();
    nextExpectedChunkRef.current = 1;
    processingChunksRef.current = false;

    // Reset audio context timing
    nextPlayTimeRef.current = 0;

    // Close and recreate audio context to stop all audio
    if (audioContextRef.current) {
      try {
        audioContextRef.current.close();
      } catch (e) {
        console.warn("Could not close audio context:", e);
      }
      audioContextRef.current = null;
    }

    console.log("🛑 All audio stopped and buffers cleared");
  };

  /**
   * Connect to backend WebSocket for calling agent
   */
  const connectWebSocket = async () => {
    try {
      const wsProtocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      const wsHost = import.meta.env.VITE_WS_HOST || window.location.hostname;
      const wsPort = import.meta.env.VITE_WS_PORT || "8080";

      // Use a special handler_type for calling agent
      let wsUrl = `${wsProtocol}//${wsHost}:${wsPort}/ws?session_id=${sessionIdRef.current}&user_id=${userId}&handler_type=calling_agent&persona=${selectedPersona}`;

      // Append selected KB file IDs if any
      if (selectedKBFilesRef.current.length > 0) {
        wsUrl += `&kb_file_ids=${encodeURIComponent(JSON.stringify(selectedKBFilesRef.current))}`;
      }

      // Append selected external collection IDs if any
      if (selectedExtCollectionsRef.current.length > 0) {
        wsUrl += `&kb_collection_ids=${encodeURIComponent(JSON.stringify(selectedExtCollectionsRef.current))}`;
      }

      console.log("🔌 Connecting to WebSocket:", wsUrl);

      const ws = new WebSocket(wsUrl);
      websocketRef.current = ws;

      ws.onopen = () => {
        console.log("✅ WebSocket connected");
        setConnectionStatus("connected");
        // Tell the auth layer a call is live so it won't tear the session
        // down underneath us (see services/meetingSession.js).
        setMeetingActive(true);
      };

      ws.onmessage = async (event) => {
        try {
          // Check if message is empty or not a string
          if (!event.data || event.data.trim() === "") {
            console.warn("⚠️ Received empty WebSocket message");
            return;
          }

          console.log("📨 Raw message received:", event.data.substring(0, 200));

          const data = JSON.parse(event.data);
          console.log("📩 WebSocket message parsed:", {
            type: data.type,
            hasAudio: !!data.audio_base64,
            audioLength: data.audio_base64?.length,
            textLength: data.text?.length,
            text: data.text?.substring(0, 50),
          });

          if (data.type === "agent_response") {
            // Add agent response to transcript
            addTranscript("Agent", data.text, "agent");

            // Show status that agent is speaking
            setStatus({ type: "info", message: "🎙️ Agent speaking..." });

            // Play audio if available (backward compatibility for non-streaming)
            if (data.audio_base64) {
              console.log(
                "🔊 Playing full audio (non-streaming), length:",
                data.audio_base64.length,
              );
              playAudioFromBase64(data.audio_base64);
            } else {
              console.warn("⚠️ No audio in agent response");
            }
          } else if (data.type === "agent_response_streaming") {
            // 🔥 STREAMING TEXT: Update transcript as text streams in (shows what's being spoken)
            console.log(
              `📝 Streaming text: "${data.text.substring(0, 30)}..."`,
              data.is_final ? "(final)" : "",
            );

            // Update or add transcript entry
            setTranscripts((prev) => {
              const lastTranscript = prev[prev.length - 1];
              if (
                lastTranscript &&
                lastTranscript.speaker === "Agent" &&
                lastTranscript.streaming
              ) {
                // Update existing streaming transcript
                const updated = [...prev];
                updated[updated.length - 1] = {
                  ...lastTranscript,
                  text: data.text,
                  streaming: !data.is_final,
                };
                return updated;
              } else {
                // Add new streaming transcript entry
                return [
                  ...prev,
                  {
                    id: Date.now(),
                    speaker: "Agent",
                    text: data.text,
                    type: "agent",
                    timestamp: new Date().toLocaleTimeString(),
                    streaming: !data.is_final,
                  },
                ];
              }
            });

            if (!data.is_final) {
              setStatus({ type: "info", message: "🎙️ Agent speaking..." });
            }
          } else if (data.type === "audio_chunk") {
            // 🔥 REAL-TIME STREAMING: Play audio chunks in sequence
            console.log(
              `🎵 Received chunk #${data.chunk_number}${data.total_chunks ? ` of ${data.total_chunks}` : ''}, is_final: ${data.is_final}`,
            );

            if (data.audio_chunk) {
              // Queue chunk with proper sequencing to prevent out-of-order playback
              await queueAudioChunk(data.audio_chunk, data.chunk_number);
            }

            if (data.is_final) {
              const totalChunks = data.total_chunks || data.chunk_number;
              console.log(
                `✅ Final marker received: expecting ${totalChunks} total chunks, last seen: ${data.chunk_number}`,
              );
              
              // Clear any pending flush timer
              if (accumulatorTimerRef.current) {
                clearTimeout(accumulatorTimerRef.current);
                accumulatorTimerRef.current = null;
              }
              
              // Force immediate flush of accumulated chunks
              await flushAccumulatedChunks();
              
              // Wait for any late-arriving chunks
              setTimeout(async () => {
                // Try to process any remaining buffered chunks
                await processBufferedChunks();
                
                // Final flush of any remaining accumulated chunks
                await flushAccumulatedChunks();
                
                // Check for missing chunks
                const expectedChunk = nextExpectedChunkRef.current;
                const missingCount = totalChunks - expectedChunk + 1;
                
                if (chunkBufferRef.current.size > 0) {
                  const bufferedChunks = Array.from(chunkBufferRef.current.keys()).sort((a, b) => a - b);
                  console.warn(`⚠️ ${chunkBufferRef.current.size} chunks still buffered: ${bufferedChunks.join(', ')}`);
                  
                  // Force play remaining chunks even if out of order (prevents cutting off last words)
                  console.warn(`⚠️ Force-playing ${chunkBufferRef.current.size} buffered chunks to prevent cutoff`);
                  const sortedChunks = Array.from(chunkBufferRef.current.entries()).sort((a, b) => a[0] - b[0]);
                  for (const [num, chunk] of sortedChunks) {
                    console.warn(`⚠️ Force-playing chunk #${num}`);
                    chunkAccumulatorRef.current.push(chunk);
                  }
                  await flushAccumulatedChunks();
                } else if (expectedChunk <= totalChunks) {
                  console.log(`✅ All ${totalChunks} chunks received and played successfully`);
                } else {
                  console.warn(`⚠️ Received more chunks (${expectedChunk - 1}) than expected (${totalChunks})`);
                }
                
                // Reset for next audio stream
                chunkBufferRef.current.clear();
                nextExpectedChunkRef.current = 1;
                
                setStatus({
                  type: "success",
                  message: "🎙️ Agent finished speaking",
                });
              }, 500); // Wait 500ms for any late chunks
            }
          } else if (data.type === "connected") {
            // Connection confirmed, waiting for greeting
            console.log("✅ Connected, waiting for greeting:", data.message);
            setStatus({
              type: "success",
              message: "📞 Connected, waiting for greeting...",
            });
          } else if (data.type === "agent_audio_chunk") {
            // Stream audio chunks
            if (data.audio_base64) {
              queueAudioChunk(data.audio_base64);
            }
          } else if (data.type === "error") {
            console.error("❌ WebSocket error:", data.message);
            setStatus({ type: "error", message: data.message });
          }
        } catch (error) {
          console.error("⚠️ Could not parse WebSocket message:", error);
          console.error("⚠️ Message data:", event.data?.substring(0, 500));
        }
      };

      ws.onerror = (error) => {
        console.error("❌ WebSocket error:", error);
        setConnectionStatus("error");
        setStatus({
          type: "error",
          message: "❌ Connection error. Please try again.",
        });
      };

      ws.onclose = () => {
        console.log("🔌 WebSocket disconnected");
        setConnectionStatus("disconnected");
        // Call is over (or dropped) — a deferred logout may now proceed.
        setMeetingActive(false);
      };
    } catch (error) {
      console.error("❌ Failed to connect WebSocket:", error);
      setStatus({
        type: "error",
        message: "⚠️ Could not connect to server",
      });
    }
  };

  /**
   * Fetch Azure Speech SDK configuration from backend (same as NewMeeting)
   */
  const fetchSpeechConfig = async () => {
    try {
      const baseURL = import.meta.env.VITE_API_URL || "http://localhost:8080";
      const response = await fetch(`${baseURL}/api/speech-config`);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to fetch speech config");
      }

      const data = await response.json();
      console.log("📡 Speech config received:", data);

      // Handle backend response format: { body: { token, region } }
      if (data.body) {
        return data.body;
      }

      // Fallback for direct format: { token, region }
      return data;
    } catch (error) {
      console.error("❌ Failed to fetch speech config:", error);
      throw error;
    }
  };

  /**
   * Setup Azure Speech Recognition (exact same as NewMeeting)
   */
  const setupSpeechRecognition = async () => {
    try {
      // Fetch Azure Speech config from backend
      const speechConfig = await fetchSpeechConfig();

      // Check if Azure Speech SDK is loaded (from CDN)
      if (typeof SpeechSDK === "undefined") {
        throw new Error(
          "Speech recognition service not loaded. Please refresh the page.",
        );
      }

      // Validate speech config
      if (!speechConfig || !speechConfig.token || !speechConfig.region) {
        throw new Error(
          "Speech recognition service is not properly configured. Please contact support.",
        );
      }

      // Create Speech SDK configuration using token (same as NewMeeting)
      const azureSpeechConfig = SpeechSDK.SpeechConfig.fromAuthorizationToken(
        speechConfig.token,
        speechConfig.region,
      );
      azureSpeechConfig.speechRecognitionLanguage = "en-US";

      // Create audio config from microphone stream
      const audioConfig = SpeechSDK.AudioConfig.fromStreamInput(
        micStreamRef.current,
      );

      // Use SpeechRecognizer for single speaker (simpler than ConversationTranscriber)
      const recognizer = new SpeechSDK.SpeechRecognizer(
        azureSpeechConfig,
        audioConfig,
      );

      recognizerRef.current = recognizer;

      // Session started
      recognizer.sessionStarted = () => {
        console.log("✅ Azure Speech session started");
      };

      // Session stopped
      recognizer.sessionStopped = () => {
        console.log("⏹️ Azure Speech session stopped");
      };

      // Handle recognized speech (final)
      recognizer.recognized = (s, e) => {
        if (e.result.reason === SpeechSDK.ResultReason.RecognizedSpeech) {
          const text = e.result.text;
          if (text && text.trim()) {
            console.log("🎤 Recognized:", text);

            // Stop any playing audio when user speaks (prevent overlap)
            stopAllAudio();

            addTranscript("You", text, "user");
            sendTextToAgent(text);
          }
        }
      };

      // Handle errors
      recognizer.canceled = (s, e) => {
        console.error("❌ Azure Speech canceled:", e.reason);
        if (e.reason === SpeechSDK.CancellationReason.Error) {
          console.error("Error details:", e.errorDetails);
          setStatus({
            type: "error",
            message: "❌ Transcription error. Please try again.",
          });
        }
      };

      // Start continuous recognition
      recognizer.startContinuousRecognitionAsync(
        () => {
          console.log("✅ Speech recognition started");
        },
        (err) => {
          console.error("❌ Failed to start recognition:", err);
          throw new Error("Failed to start speech recognition");
        },
      );
    } catch (error) {
      console.error("❌ Failed to setup speech recognition:", error);
      throw error;
    }
  };

  /**
   * Send transcribed text to backend
   */
  const sendTextToAgent = (text) => {
    if (websocketRef.current?.readyState === WebSocket.OPEN) {
      websocketRef.current.send(
        JSON.stringify({
          type: "user_message",
          text: text,
          session_id: sessionIdRef.current,
          timestamp: Date.now(),
        }),
      );
    }
  };

  /**
   * Add transcript to conversation history
   */
  const addTranscript = (speaker, text, type) => {
    setTranscripts((prev) => [
      ...prev,
      {
        id: Date.now(),
        speaker,
        text,
        type,
        timestamp: new Date().toLocaleTimeString(),
      },
    ]);
  };

  /**
   * Setup audio visualization
   */
  const setupAudioVisualization = (stream) => {
    try {
      const audioContext = new (
        window.AudioContext || window.webkitAudioContext
      )();
      const analyser = audioContext.createAnalyser();
      const microphone = audioContext.createMediaStreamSource(stream);

      analyser.fftSize = 256;
      microphone.connect(analyser);

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;

      // Animate audio level
      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      const updateLevel = () => {
        if (!analyserRef.current) return;

        analyser.getByteFrequencyData(dataArray);
        const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
        setAudioLevel(Math.min(100, (average / 128) * 100));

        requestAnimationFrame(updateLevel);
      };
      updateLevel();
    } catch (error) {
      console.error("❌ Failed to setup audio visualization:", error);
    }
  };

  /**
   * Play audio from base64 string (MP3 from ElevenLabs)
   */
  const playAudioFromBase64 = async (base64Audio) => {
    try {
      if (isSpeakerMuted) {
        console.log("🔇 Speaker muted, skipping audio playback");
        return;
      }

      console.log("🔊 Playing audio, length:", base64Audio.length);

      // Decode base64 to binary
      const binaryString = atob(base64Audio);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      // Create blob from binary data (MP3 format)
      const blob = new Blob([bytes.buffer], { type: "audio/mpeg" });
      const url = URL.createObjectURL(blob);

      // Create audio element and play
      const audio = new Audio(url);

      audio.onended = () => {
        URL.revokeObjectURL(url);
        console.log("✅ Audio playback completed");
      };

      audio.onerror = (error) => {
        console.error("❌ Audio playback error:", error);
        URL.revokeObjectURL(url);
      };

      await audio.play();
      console.log("✅ Audio playing...");
    } catch (error) {
      console.error("❌ Failed to play audio:", error);
      setStatus({
        type: "error",
        message: "Failed to play audio response",
      });
    }
  };

  /**
   * Toggle microphone mute
   */
  const toggleMute = () => {
    if (recognizerRef.current) {
      if (isMuted) {
        recognizerRef.current.startContinuousRecognitionAsync();
      } else {
        recognizerRef.current.stopContinuousRecognitionAsync();
      }
    }
    setIsMuted(!isMuted);
  };

  /**
   * Toggle speaker mute
   */
  const toggleSpeaker = () => {
    setIsSpeakerMuted(!isSpeakerMuted);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-800 mb-2">
                AI Calling Agent
              </h1>
              <p className="text-gray-600">
                Real-time voice conversation with AI assistant
              </p>
            </div>
            <div className="flex items-center gap-4">
              {/* Connection Status */}
              <div className="flex items-center gap-2">
                <div
                  className={`w-3 h-3 rounded-full ${
                    connectionStatus === "connected"
                      ? "bg-green-500"
                      : connectionStatus === "error"
                        ? "bg-red-500"
                        : "bg-gray-400"
                  }`}
                />
                <span className="text-sm font-medium text-gray-600">
                  {connectionStatus === "connected"
                    ? "Connected"
                    : connectionStatus === "error"
                      ? "Error"
                      : "Disconnected"}
                </span>
              </div>
            </div>
          </div>

          {/* Status Message */}
          {status.message && (
            <div
              className={`mt-4 p-4 rounded-xl flex items-center gap-3 ${
                status.type === "success"
                  ? "bg-green-50 text-green-800 border border-green-200"
                  : status.type === "error"
                    ? "bg-red-50 text-red-800 border border-red-200"
                    : "bg-blue-50 text-blue-800 border border-blue-200"
              }`}
            >
              {status.type === "success" ? (
                <CheckCircle size={20} />
              ) : status.type === "error" ? (
                <AlertCircle size={20} />
              ) : (
                <MessageSquare size={20} />
              )}
              <span className="font-medium">{status.message}</span>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Call Controls */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-xl p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-6">
                Call Controls
              </h2>

              {/* Persona Selector — only shown before call starts */}
              {!isCallActive && (
                <div className="mb-6">
                  <p className="text-sm font-semibold text-gray-600 mb-3">Select Persona</p>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => setSelectedPersona("sales")}
                      className={`py-3 px-4 rounded-xl border-2 font-medium text-sm transition-all flex flex-col items-center gap-1 ${
                        selectedPersona === "sales"
                          ? "border-purple-500 bg-purple-50 text-purple-700"
                          : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"
                      }`}
                    >
                      <Bot size={18} />
                      Sales Agent
                      <span className="text-xs opacity-70 font-normal">plays the agent</span>
                    </button>
                    <button
                      onClick={() => setSelectedPersona("client")}
                      className={`py-3 px-4 rounded-xl border-2 font-medium text-sm transition-all flex flex-col items-center gap-1 ${
                        selectedPersona === "client"
                          ? "border-blue-500 bg-blue-50 text-blue-700"
                          : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"
                      }`}
                    >
                      <User size={18} />
                      Client
                      <span className="text-xs opacity-70 font-normal">plays the customer</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Knowledge Base Selection — only shown before call starts and for sales persona */}
              {!isCallActive && selectedPersona === "sales" && (
                <div className="mb-6">
                  <p className="text-sm font-semibold text-gray-600 mb-3 flex items-center gap-2">
                    <BookOpen size={16} />
                    Knowledge Base (Optional)
                  </p>
                  {loadingKB ? (
                    <div className="text-sm text-gray-500 text-center py-4 border border-gray-200 rounded-xl bg-gray-50">
                      Loading knowledge base files...
                    </div>
                  ) : knowledgeBaseFiles.length > 0 ? (
                    <>
                      <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-xl bg-gray-50 p-2 space-y-1">
                        {knowledgeBaseFiles.map((file) => (
                          <div
                            key={file.id}
                            onClick={() => toggleKBFile(file.id)}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-all text-sm ${
                              selectedKBFiles.includes(file.id)
                                ? "bg-purple-50 border border-purple-300"
                                : "bg-white border border-gray-100 hover:border-gray-300"
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={selectedKBFiles.includes(file.id)}
                              onChange={() => toggleKBFile(file.id)}
                              onClick={(e) => e.stopPropagation()}
                              className="w-4 h-4 accent-purple-500"
                            />
                            <FileText size={14} className="text-purple-500 flex-shrink-0" />
                            <span className="truncate text-gray-700" title={file.file_name}>
                              {file.file_name}
                            </span>
                          </div>
                        ))}
                      </div>
                      <div className="flex items-center justify-between mt-2">
                        <button
                          onClick={() => {
                            if (selectedKBFiles.length === knowledgeBaseFiles.length) {
                              setSelectedKBFiles([]);
                            } else {
                              setSelectedKBFiles(knowledgeBaseFiles.map((f) => f.id));
                            }
                          }}
                          className="text-xs text-purple-600 font-medium hover:text-purple-800"
                        >
                          {selectedKBFiles.length === knowledgeBaseFiles.length ? "Deselect All" : "Select All"}
                        </button>
                        <span className="text-xs text-gray-500">
                          {selectedKBFiles.length} selected
                        </span>
                      </div>
                    </>
                  ) : (
                    <div className="text-sm text-gray-500 text-center py-4 border border-gray-200 rounded-xl bg-gray-50">
                      No KB files found. Upload files in Knowledge Base.
                    </div>
                  )}
                </div>
              )}

              {/* External Qdrant Collections — only shown before call starts and for sales persona */}
              {!isCallActive && selectedPersona === "sales" && externalCollections.length > 0 && (
                <div className="mb-6">
                  <p className="text-sm font-semibold text-gray-600 mb-3 flex items-center gap-2">
                    <BookOpen size={16} />
                    External Collections (Optional)
                  </p>
                  <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-xl bg-gray-50 p-2 space-y-1">
                    {externalCollections.map((col) => (
                      <div
                        key={col.id}
                        onClick={() => toggleExtCollection(col.id)}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-all text-sm ${
                          selectedExtCollections.includes(col.id)
                            ? "bg-indigo-50 border border-indigo-300"
                            : "bg-white border border-gray-100 hover:border-gray-300"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedExtCollections.includes(col.id)}
                          onChange={() => toggleExtCollection(col.id)}
                          onClick={(e) => e.stopPropagation()}
                          className="w-4 h-4 accent-indigo-500"
                        />
                        <FileText size={14} className="text-indigo-500 flex-shrink-0" />
                        <div className="flex flex-col min-w-0">
                          <span className="truncate text-gray-700" title={col.display_name}>
                            {col.display_name}
                          </span>
                          <span className="text-[10px] text-gray-400 truncate">
                            {col.collection_name} &middot; {col.qdrant_url}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <button
                      onClick={() => {
                        if (selectedExtCollections.length === externalCollections.length) {
                          setSelectedExtCollections([]);
                        } else {
                          setSelectedExtCollections(externalCollections.map((c) => c.id));
                        }
                      }}
                      className="text-xs text-indigo-600 font-medium hover:text-indigo-800"
                    >
                      {selectedExtCollections.length === externalCollections.length ? "Deselect All" : "Select All"}
                    </button>
                    <span className="text-xs text-gray-500">
                      {selectedExtCollections.length} selected
                    </span>
                  </div>
                </div>
              )}

              {/* Main Call Button */}
              <button
                onClick={isCallActive ? stopCall : startCall}
                disabled={!userId}
                className={`w-full py-6 rounded-2xl font-bold text-lg mb-6 transition-all flex items-center justify-center gap-3 ${
                  isCallActive
                    ? "bg-red-500 hover:bg-red-600 text-white shadow-lg"
                    : "bg-green-500 hover:bg-green-600 text-white shadow-lg disabled:bg-gray-300 disabled:cursor-not-allowed"
                }`}
              >
                {isCallActive ? (
                  <>
                    <PhoneOff size={24} />
                    End Call
                  </>
                ) : (
                  <>
                    <Phone size={24} />
                    Start Call
                  </>
                )}
              </button>

              {/* Audio Controls */}
              {isCallActive && (
                <div className="space-y-4">
                  <button
                    onClick={toggleMute}
                    className={`w-full py-4 rounded-xl font-medium flex items-center justify-center gap-3 transition-all ${
                      isMuted
                        ? "bg-red-100 text-red-700 border-2 border-red-300"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    {isMuted ? <MicOff size={20} /> : <Mic size={20} />}
                    {isMuted ? "Unmute Mic" : "Mute Mic"}
                  </button>

                  <button
                    onClick={toggleSpeaker}
                    className={`w-full py-4 rounded-xl font-medium flex items-center justify-center gap-3 transition-all ${
                      isSpeakerMuted
                        ? "bg-red-100 text-red-700 border-2 border-red-300"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    {isSpeakerMuted ? (
                      <VolumeX size={20} />
                    ) : (
                      <Volume2 size={20} />
                    )}
                    {isSpeakerMuted ? "Unmute Speaker" : "Mute Speaker"}
                  </button>
                </div>
              )}

              {/* Audio Level Indicator */}
              {isCallActive && !isMuted && (
                <div className="mt-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-600">
                      Microphone Level
                    </span>
                    <span className="text-sm font-bold text-gray-800">
                      {Math.round(audioLevel)}%
                    </span>
                  </div>
                  <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-green-400 to-green-600 transition-all duration-100"
                      style={{ width: `${audioLevel}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Conversation Transcript */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-xl p-6 h-[600px] flex flex-col">
              <h2 className="text-xl font-bold text-gray-800 mb-4">
                Conversation
              </h2>

              <div
                ref={transcriptContainerRef}
                className="flex-1 overflow-y-auto space-y-4 pr-2"
              >
                {transcripts.length === 0 ? (
                  <div className="h-full flex items-center justify-center text-gray-400">
                    <div className="text-center">
                      <MessageSquare size={48} className="mx-auto mb-4" />
                      <p>Start a call to begin conversation</p>
                    </div>
                  </div>
                ) : (
                  transcripts.map((transcript) => (
                    <div
                      key={transcript.id}
                      className={`flex gap-3 ${
                        transcript.type === "agent"
                          ? "justify-start"
                          : "justify-end"
                      }`}
                    >
                      {transcript.type === "agent" && (
                        <div className="w-10 h-10 rounded-full bg-purple-500 flex items-center justify-center flex-shrink-0">
                          <Bot size={20} className="text-white" />
                        </div>
                      )}
                      <div
                        className={`max-w-[70%] rounded-2xl px-4 py-3 ${
                          transcript.type === "agent"
                            ? "bg-purple-100 text-purple-900"
                            : "bg-blue-500 text-white"
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-bold text-sm">
                            {transcript.speaker}
                          </span>
                          <span className="text-xs opacity-70">
                            {transcript.timestamp}
                          </span>
                        </div>
                        <p className="text-sm leading-relaxed">
                          {transcript.text}
                        </p>
                      </div>
                      {transcript.type === "user" && (
                        <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                          <User size={20} className="text-white" />
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CallingAgent;
